/**
 * **A10 · The chair's area one-pager** (Stream A, Paper.dc.html): what a chair holds all weekend —
 * the three things, the area's budget lines (budget · spent · left, from Budget), the roster by day
 * (filled of seats, from Volunteers) and who to call (Dispatch and Security from the radio plan,
 * the finance director from Roles). Printed Thursday; the chair app is the live copy. What CIDER
 * does not hold — the area's place on the grounds, the booth's phone — prints NO DATA.
 */
import { festivalDayWords } from './count_papers.js';
import { NO_DATA, type PaperBlock, type PaperPage } from './paper_page.js';

export interface AreaPageInput {
  year: number;
  area: string | null;
  /** The area's chairs from Roles. */
  chairs: string[];
  /** The budget's lines for the area; null when Budget did not answer. */
  lines:
    { line: string; budgetCents: number; spentCents: number; leftCents: number | null }[] | null;
  /** Each published shift: its operating day, its times in words, who is on it, filled of seats. */
  shifts: { day: string; time: string; who: string; filled: number; seats: number }[];
  /** From the radio plan; null without one. */
  plan: {
    chairsChannel: string | null;
    securityChannel: string | null;
    dispatchPhone: string | null;
  } | null;
  financeDirector: string | null;
}

export function areaPage(i: AreaPageInput): PaperPage {
  const area = i.area ?? NO_DATA;
  // Roles records chairs, not co-chairs (canvas defect 324): "Karen Duda · Dale Pelc, chairs".
  const who = i.chairs.length
    ? `${i.chairs.join(' · ')}, ${i.chairs.length > 1 ? 'chairs' : 'chair'}`
    : `Chair ${NO_DATA}`;
  const ch = i.plan?.chairsChannel ?? NO_DATA;
  const days = [...new Set(i.shifts.map((s) => s.day))].sort();
  const roster: PaperBlock[] = days.length
    ? days.map((d) => ({
        head: `ROSTER · ${(festivalDayWords(d).split(' ')[0] ?? '').toUpperCase()}`,
        cols: '80px 1fr 60px',
        rows: i.shifts
          .filter((s) => s.day === d)
          .map((s) => [s.time, s.who, `${String(s.filled)} of ${String(s.seats)}`]),
      }))
    : [{ head: 'ROSTER', cols: '80px 1fr 60px', rows: [['', 'No published shift yet', '']] }];
  return {
    title: `${area} · ${String(i.year)}`,
    sub: `${who} · ${NO_DATA} · Channel ${ch}`,
    qr: `AREA ${area.toUpperCase()}`,
    blocks: [
      {
        head: 'THE THREE THINGS',
        cols: '1fr 1fr 1fr',
        rows: [
          ['Ask for more', 'I spent', 'Open a PO'],
          ['a budget change, in your words', 'a receipt against a line', 'they’ll bill us'],
        ],
      },
      {
        head: 'YOUR LINES',
        cols: '1fr 90px 90px 90px',
        rows: [
          ['', 'BUDGET', 'SPENT', 'LEFT'],
          ...(i.lines === null
            ? [[NO_DATA, '', '', '']]
            : i.lines.length
              ? i.lines.map((l) => [l.line, l.budgetCents, l.spentCents, l.leftCents ?? NO_DATA])
              : [['No lines on this year’s budget', '', '', '']]),
        ],
      },
      ...roster,
      {
        head: 'WHO TO CALL',
        cols: '1fr 1fr',
        rows: [
          ['Dispatch', `${i.plan?.dispatchPhone ?? NO_DATA} · Channel ${ch}`],
          ['Security', `Channel ${i.plan?.securityChannel ?? NO_DATA}`],
          ['The booth · reassign · a hand', NO_DATA],
          [`Finance director · ${i.financeDirector ?? NO_DATA}`, NO_DATA],
        ],
      },
    ],
    foot: `Area one-pager · ${area} · printed Thursday · the chair app is the live copy`,
  };
}
